import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { GrLinkPrevious } from "react-icons/gr";
import { programs } from "../../../data/programs";
import _ from "lodash";
import api from "../../../config.json";
import { motion, AnimatePresence } from "framer-motion";

const url = api.url;

const SingleStaff = (props) => {
  const navigate = useNavigate();
  let { state } = useLocation();
  const s = state.staff;
  const [isVisible, setIsVisible] = useState(false);
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        when: "beforeChildren"
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };
  
  const hoverEffect = {
    scale: 1.03,
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    transition: { duration: 0.3 }
  };
  
  const tapEffect = { scale: 0.98 };
  
  const floatingAnimation = {
    y: [0, -10, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  useEffect(() => {
    setIsVisible(true);
    return () => setIsVisible(false);
  }, []);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="w-full xl:pt-[95px] md:pt-[95px] sm:pt-[95px] xs:pt-[75px] relative group flex flex-col justify-center items-center sm:mx-5 xs:mx-5"
        >
          <div className="grid grid-rows-1 mt-20 text-black md:w-[60%] xl:w-[60%]">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <div className="flex flex-row sm:flex-col xs:flex-col space-y-1">
                <motion.div
                  variants={itemVariants}
                  whileHover={hoverEffect}
                  whileTap={tapEffect}
                  className="relative overflow-hidden rounded-2xl"
                >
                  <img
                    src={url + s.staff.avatar}
                    alt="avatar"
                    className="h-[250px] w-[250px] rounded-2xl object-cover"
                  />
                  <motion.div 
                    className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-end p-4"
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                  >
                    <p className="text-white text-sm">View Profile</p>
                  </motion.div>
                </motion.div>
                
                <motion.div 
                  className="mt-10 md:mx-8 xl:mx-8 space-y-3 flex flex-col justify-center"
                  variants={itemVariants}
                >
                  <p className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-500">
                    {s.staff.title}. {s.staff.fullName}
                  </p>
                  <motion.p 
                    className="text-lg font-medium"
                    animate={floatingAnimation}
                  >
                    {s.occupation}
                  </motion.p>
                  <p className="text-base flex items-center">
                    <span className="mr-2">📱</span> {s.staff.mobileNumber}
                  </p>
                  <p className="text-base flex items-center">
                    <span className="mr-2">✉️</span> {s.staff.email}
                  </p>
                </motion.div>
              </div>

              <motion.div 
                className="mt-5"
                variants={itemVariants}
              >
                <div>
                  <p className="font-bold text-xl my-3 bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">
                    Biography
                  </p>
                  <div className="w-full text-lg tracking-wide">
                    <motion.p 
                      className="md:ml-5 xl:ml-5 my-5 xs:text-justify whitespace-pre-line bg-white p-6 rounded-xl shadow-md border-l-4 border-blue-500"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      {s.staff.bio}
                    </motion.p>
                  </div>
                </div>

                {s.staff.reserachInterest.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    <p className="font-bold text-xl my-4 bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">
                      Research Areas
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {s.staff.reserachInterest.map((d, i) => (
                        <motion.div
                          key={i}
                          className="ml-5 my-2 p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.5 + i * 0.1 }}
                          whileHover={{
                            scale: 1.02,
                            backgroundColor: "rgba(219, 234, 254, 0.5)"
                          }}
                        >
                          <span className="mr-2 text-blue-500">•</span> {d}
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </motion.div>
            </motion.div>
          </div>
          
          <motion.div 
            className="w-full flex justify-start items-center p-6 text-2xl md:w-[60%] xl:w-[60%]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <motion.p
              onClick={() => navigate(-1)}
              className="rounded-full w-fit border-2 border-blue-600 p-2 bg-white cursor-pointer shadow-md"
              whileHover={{ 
                scale: 1.1,
                backgroundColor: "#3b82f6",
                color: "white"
              }}
              whileTap={{ scale: 0.9 }}
            >
              <GrLinkPrevious />
            </motion.p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SingleStaff;